/**
 * Single call site for venue reservations.
 *
 * Every reservation request in the app goes through here so the endpoint shapes
 * live in one place — the backend controller is still to be built, and the mock
 * API (src/mock/mockReservations.ts) answers these paths today.
 */
import {
    Reservation,
    ReservationAvailabilitySlot,
    ReservationClash,
    ReservationFieldDef,
    ReservationGuest,
    ReservationSettings,
    ReservationStatus,
} from '../interfaces';

const API_URL = process.env.REACT_APP_API_URL || '';

export interface ReservationFilter {
    date?: string;
    dateFrom?: string;
    dateTo?: string;
    floorId?: string;
    statuses?: ReservationStatus[];
    searchTerm?: string;
}

/**
 * The result of anything that puts a booking on a table.
 *
 * A refusal is a normal outcome rather than an error: the server owns the rule
 * (it can see the whole day and every floor, and other devices book while this
 * one is open) and hands back what it collided with so the UI can name it. An
 * empty `clashes` list means the save failed for some other reason.
 */
export type ReservationSaveResult =
    | { ok: true; reservation: Reservation }
    | { ok: false; clashes: ReservationClash[] };

async function get<T>(path: string, fallback: T): Promise<T> {
    try {
        const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
        if (!res.ok) return fallback;
        const data = await res.json();
        return (data ?? fallback) as T;
    } catch (e) {
        console.warn('[ReservationService] GET failed', path, e);
        return fallback;
    }
}

async function post<T>(path: string, body: unknown, fallback: T): Promise<T> {
    try {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body ?? {}),
        });
        if (!res.ok) return fallback;
        const data = await res.json();
        return (data ?? fallback) as T;
    } catch (e) {
        console.warn('[ReservationService] POST failed', path, e);
        return fallback;
    }
}

async function postSave(path: string, body: unknown): Promise<ReservationSaveResult> {
    try {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body ?? {}),
        });
        if (res.status === 409) {
            const data = await res.json().catch(() => null);
            return { ok: false, clashes: (data?.clashes || []) as ReservationClash[] };
        }
        if (!res.ok) return { ok: false, clashes: [] };
        const data = await res.json();
        return data ? { ok: true, reservation: data as Reservation } : { ok: false, clashes: [] };
    } catch (e) {
        console.warn('[ReservationService] save failed', path, e);
        return { ok: false, clashes: [] };
    }
}

export const ReservationService = {
    /** Admin-defined field definitions that drive the booking form. */
    getFieldDefinitions(): Promise<ReservationFieldDef[]> {
        return get<ReservationFieldDef[]>('/ReservationField', []);
    },

    /**
     * Admin: replace the whole definition set. Sent as one list rather than per
     * field because display order is part of what's being saved.
     */
    saveFieldDefinitions(fields: ReservationFieldDef[]): Promise<ReservationFieldDef[] | null> {
        return post<ReservationFieldDef[] | null>('/ReservationField/save', fields, null);
    },

    getSettings(): Promise<ReservationSettings | null> {
        return get<ReservationSettings | null>('/ReservationSettings', null);
    },

    saveSettings(settings: ReservationSettings): Promise<ReservationSettings | null> {
        return post<ReservationSettings | null>('/ReservationSettings/save', settings, null);
    },

    filter(filter: ReservationFilter): Promise<Reservation[]> {
        return post<Reservation[]>('/Reservation/filter', filter, []);
    },

    getById(id: string): Promise<Reservation | null> {
        return get<Reservation | null>(`/Reservation/${encodeURIComponent(id)}`, null);
    },

    /**
     * Which times still have a table big enough for the party, and which tables.
     * `excludeId` is the booking being edited, so it does not block its own slot.
     */
    availability(
        date: string,
        partySize: number,
        floorId?: string,
        excludeId?: string,
    ): Promise<ReservationAvailabilitySlot[]> {
        const q = new URLSearchParams({ date, partySize: String(partySize) });
        if (floorId) q.set('floorId', floorId);
        if (excludeId) q.set('excludeId', excludeId);
        return get<ReservationAvailabilitySlot[]>(`/Reservation/availability?${q.toString()}`, []);
    },

    /**
     * Takes or amends a booking. Refused when it would put two parties on one
     * table at the same time, unless `allowOverlap` says the caller means it.
     */
    save(
        reservation: Partial<Reservation>,
        allowOverlap = false,
    ): Promise<ReservationSaveResult> {
        const path = reservation.id ? '/Reservation/update' : '/Reservation';
        return postSave(path, { ...reservation, allowOverlap });
    },

    /** Patch an existing booking without touching its time or tables. */
    async update(reservation: Partial<Reservation>): Promise<Reservation | null> {
        const result = await postSave('/Reservation/update', reservation);
        return result.ok ? result.reservation : null;
    },

    /** Put a walk-in party into the queue with the wait they were quoted. */
    joinWaitlist(entry: {
        partySize: number;
        quotedMinutes: number;
        guest: ReservationGuest;
        guestNotes?: string;
        floorId?: string;
        customFields?: Record<string, unknown>;
    }): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/waitlist', entry, null);
    },

    /** Re-quote a wait, or record that the party has been called to their table. */
    updateWaitlist(
        id: string,
        patch: { quotedMinutes?: number; notified?: boolean },
    ): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/waitlist/update', { id, ...patch }, null);
    },

    /** Move a booking onto (or off) a set of tables without changing its status. */
    assignTables(
        id: string,
        tableIds: string[],
        floorId?: string,
        allowOverlap = false,
    ): Promise<ReservationSaveResult> {
        return postSave('/Reservation/assign', { id, tableIds, floorId, allowOverlap });
    },

    confirm(id: string): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/confirm', { id }, null);
    },

    markArrived(id: string): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/arrived', { id }, null);
    },

    /** Called once the party is seated and an order has been opened. */
    seat(id: string, orderId?: string, tableIds?: string[]): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/seat', { id, orderId, tableIds }, null);
    },

    complete(id: string): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/complete', { id }, null);
    },

    markNoShow(id: string): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/no-show', { id }, null);
    },

    cancel(id: string, reason?: string): Promise<Reservation | null> {
        return post<Reservation | null>('/Reservation/cancel', { id, reason }, null);
    },
};

export default ReservationService;
